import { useMemo } from 'react';

export interface ProductItem {
  id: number;
  productName: string;
  productCode: string;
  quantity: string;
  productId?: number;
  originalItemId?: number;
}

export interface Product {
  id: number;
  name: string;
  code: string;
}

export interface UseProductSelectionStepProps {
  productItems: ProductItem[];
  products: Product[];
  onAddRow: () => void;
  onRemoveRow: (id: number) => void;
  onUpdateItem: (id: number, field: keyof ProductItem, value: string) => void;
}

export const useProductSelectionStep = ({
  productItems,
  products,
  onAddRow,
  onRemoveRow,
  onUpdateItem,
}: UseProductSelectionStepProps) => {
  // Asegurar que products es un array válido
  const safeProducts = useMemo(() => {
    return Array.isArray(products) ? products : [];
  }, [products]);

  // Obtener IDs de productos seleccionados para un item específico
  const getSelectedProductIds = (currentItemId: number) => {
    return productItems
      .filter((item) => item.id !== currentItemId && item.productId)
      .map((item) => item.productId!)
      .filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates
  };

  // Determinar si se puede eliminar un item (más de 1 item)
  const canRemoveItem = useMemo(() => {
    return productItems.length > 1;
  }, [productItems.length]);

  // Determinar si hay productos disponibles
  const hasProducts = useMemo(() => {
    return safeProducts.length > 0;
  }, [safeProducts]);

  // Determinar si hay items de productos
  const hasProductItems = useMemo(() => {
    return productItems.length > 0;
  }, [productItems.length]);

  // Manejar agregar nueva fila
  const handleAddRow = () => {
    onAddRow();
  };

  // Manejar eliminar fila
  const handleRemoveRow = (id: number) => {
    onRemoveRow(id);
  };

  // Manejar actualizar item
  const handleUpdateItem = (id: number, field: keyof ProductItem, value: string) => {
    onUpdateItem(id, field, value);
  };

  // Obtener estadísticas de validación
  const validationStats = useMemo(() => {
    const validItems = productItems.filter(
      (item) => item.productName && item.productCode && item.quantity && Number(item.quantity) > 0,
    );

    return {
      total: productItems.length,
      valid: validItems.length,
      invalid: productItems.length - validItems.length,
      isValid: validItems.length === productItems.length && productItems.length > 0,
    };
  }, [productItems]);

  return {
    safeProducts,
    getSelectedProductIds,
    canRemoveItem,
    hasProducts,
    hasProductItems,
    handleAddRow,
    handleRemoveRow,
    handleUpdateItem,
    validationStats,
    productItems,
  };
};
